import { TFunction } from 'i18next'
import {
  MeetingType,
  UserInfo,
  MeetingStepViewId,
  InviteOptionsQuery,
  InviteOptionsQueryVariables,
  EmployeeDisplayTinyInfo,
  MeetingInviteOptionsExtended,
  NonParticipantsOptionsQuery,
  NonParticipantsOptionsQueryVariables,
  EmployeeWithBusinessUnit,
  AppraisalSeasonDto,
  SkillEmployeeStatusMeetingInvitationDto,
  GetEmployeeSkillStatusInMeetingQueryVariables,
  GetEmployeeSkillStatusInMeetingQuery,
  SkillEmployeeStatusDto,
} from '../../../types'
import { client } from '../../..'
import { INVITE_OPTIONS, NON_PARTICIPANTS_OPTIONS, APPRAISAL_SEASON_OPTION } from '../../../graphql/invite-options/inviteOptions'
import { GraphQLFormattedError } from 'graphql'
import { InviteModalState } from './MeetingInviteModal'
import {
  AppraisalSeasonOptionByEmployeeIdQuery,
  AppraisalSeasonOptionByEmployeeIdQueryVariables,
} from '../../../../../integration-tests/common/types'
import { EMPLOYEE_SKILL_STATUS_IN_MEETING } from '../../../graphql/skill-status/Queries'

// fetches inviteoptions via apollo client
// only used in Invite Modal
export const getInviteOptionsByEmployeeId = (
  id: number,
  inviteOptionsCallback: (any: MeetingInviteOptionsExtended) => void,
  errorCallback: (error?: ReadonlyArray<GraphQLFormattedError>) => void,
  appraisalSeasonId: number,
): void => {
  const result = client.query<InviteOptionsQuery, InviteOptionsQueryVariables>({
    query: INVITE_OPTIONS,
    variables: {
      employeeId: id,
      appraisalSeasonId,
    },
    fetchPolicy: 'no-cache',
  })

  result.then((value) => {
    const { errors, data, loading } = value

    if (!errors && !loading && data && data.meetingInviteOptionsForEmployee && data.meetingInviteOptionsForEmployee.possible) {
      inviteOptionsCallback(data.meetingInviteOptionsForEmployee)
    } else {
      errorCallback(errors)
    }
  })
}

export const getAppraisalSeasonOptionByEmployeeId = (
  employeeId: number,
  utcMeetingDate: string,
  callback: (any: AppraisalSeasonDto) => void,
  errorCallback: (error?: ReadonlyArray<GraphQLFormattedError>) => void,
): void => {
  const result = client.query<AppraisalSeasonOptionByEmployeeIdQuery, AppraisalSeasonOptionByEmployeeIdQueryVariables>({
    query: APPRAISAL_SEASON_OPTION,
    variables: {
      employeeId,
      utcMeetingDate,
    },
    fetchPolicy: 'no-cache',
  })

  result.then((value) => {
    const { errors, data, loading } = value

    if (!errors && !loading && data && data.appraisalSeasonOptionByEmployeeId) {
      callback(data.appraisalSeasonOptionByEmployeeId)
    } else {
      errorCallback(errors)
    }
  })
}

export const retrieveSkillStatusDataByEmployeeId = (
  employeeId: number,
  callback: (any?: SkillEmployeeStatusDto | null) => void,
  errorCallback: (error?: ReadonlyArray<GraphQLFormattedError>) => void,
): void => {
  const result = client.query<GetEmployeeSkillStatusInMeetingQuery, GetEmployeeSkillStatusInMeetingQueryVariables>({
    query: EMPLOYEE_SKILL_STATUS_IN_MEETING,
    variables: {
      id: employeeId,
    },
    fetchPolicy: 'no-cache',
  })

  result.then((value) => {
    const { errors, data, loading } = value
    if (!errors && !loading && !!data) {
      callback(data.getEmployeeSkillStatusInMeeting)
    } else {
      errorCallback(errors)
    }
  })
}

export const getNonParticipantsOptionsByEmployeeId = (
  id: number,
  callback: (any: EmployeeWithBusinessUnit[]) => void,
  errorCallback: (error?: ReadonlyArray<GraphQLFormattedError>) => void,
): void => {
  const result = client.query<NonParticipantsOptionsQuery, NonParticipantsOptionsQueryVariables>({
    query: NON_PARTICIPANTS_OPTIONS,
    variables: {
      employeeId: id,
    },
    fetchPolicy: 'no-cache',
  })

  result.then((value) => {
    const { errors, data, loading } = value

    if (!errors && !loading && data && data.nonParticipantsOptionsForEmployee) {
      callback(data.nonParticipantsOptionsForEmployee)
    } else {
      errorCallback(errors)
    }
  })
}

export const getSampleAgenda = (t: TFunction, meetingType: MeetingType, employee: EmployeeDisplayTinyInfo): string => {
  switch (meetingType) {
    case MeetingType.OtherGeneric:
      return t('meetingInvite.templates.agenda.other')
    case MeetingType.OtherProbationTalk:
      return t('meetingInvite.templates.agenda.probationTalk', {
        employeeFirstName: employee.firstName,
      })
    case MeetingType.OtherProjectFeedback:
      return t('meetingInvite.templates.agenda.projectFeedback')
    default:
      return ''
  }
}
//todo why UserInfo?????
export const getSampleEmail = (t: TFunction, meetingType: MeetingType, employee: EmployeeDisplayTinyInfo, inviter: UserInfo): string => {
  switch (meetingType) {
    case MeetingType.OtherGeneric:
      return t('meetingInvite.templates.email.other', {
        employeeFirstName: employee.firstName,
        reviewerFirstName: inviter.firstName,
      })
    case MeetingType.OtherProjectFeedback:
      return t('meetingInvite.templates.email.projectFeedback', {
        employeeFirstName: employee.firstName,
        reviewerFirstName: inviter.firstName,
      })

    case MeetingType.OtherProbationTalk:
      return t('meetingInvite.templates.email.probationTalk', {
        employeeFirstName: employee.firstName,
        reviewerFirstName: inviter.firstName,
      })
    case MeetingType.Appraisal:
      return t('meetingInvite.templates.email.appraisal', {
        employeeFirstName: employee.firstName,
        reviewerFirstName: inviter.firstName,
      })
    case MeetingType.AppraisalUpdate:
      return t('meetingInvite.templates.email.appraisalUpdate', {
        employeeFirstName: employee.firstName,
        reviewerFirstName: inviter.firstName,
      })
    default:
      return ''
  }
}

export const containsStepViewId = (viewIds: MeetingStepViewId[], stepId: MeetingStepViewId): boolean => {
  if (viewIds) {
    return !!viewIds.find((x) => x === stepId)
  }
  return false
}

export const getNextStepConfiguration = (
  stepId: MeetingStepViewId,
  meetingType: MeetingType,
  viewIds?: MeetingStepViewId[],
): MeetingStepViewId[] => {
  let nextStepConfiguration: MeetingStepViewId[] = viewIds ? viewIds : []
  const stepIsInStepList = !!nextStepConfiguration.find((x) => x === stepId)

  if (!stepIsInStepList) {
    nextStepConfiguration = [...nextStepConfiguration, stepId]
    if (stepId === MeetingStepViewId.PastTargets) {
      nextStepConfiguration = [...nextStepConfiguration, MeetingStepViewId.Payments]
    }
    if (meetingType === MeetingType.AppraisalUpdate && stepId === MeetingStepViewId.ContractChanges) {
      nextStepConfiguration = [...nextStepConfiguration, MeetingStepViewId.NewTargets, MeetingStepViewId.Payments]
    }
  } else {
    nextStepConfiguration = nextStepConfiguration.filter((x) => x !== stepId)
    if (stepId === MeetingStepViewId.PastTargets) {
      nextStepConfiguration = [...nextStepConfiguration, MeetingStepViewId.Payments]
    }
    if (meetingType === MeetingType.AppraisalUpdate && stepId === MeetingStepViewId.ContractChanges) {
      nextStepConfiguration = nextStepConfiguration.filter((x) => x !== MeetingStepViewId.NewTargets && x !== MeetingStepViewId.Payments)
    }
  }

  return nextStepConfiguration
}

export const updateEmailAndAgenda = (
  t: TFunction,
  nextState: InviteModalState,
  meetingType: MeetingType,
  employee: EmployeeDisplayTinyInfo,
  userInfo: UserInfo,
): InviteModalState => {
  nextState.meetingAgenda = getSampleAgenda(t, meetingType, employee)
  nextState.emailText = getSampleEmail(t, meetingType, employee, userInfo)
  return nextState
}

export const isSkillStatusBlockerInInvitation = (skillEmployeeData?: SkillEmployeeStatusMeetingInvitationDto): boolean => {
  return !!skillEmployeeData && skillEmployeeData.isSkillStatusBlocker
}

export const fullAppraisalSteps = [
  MeetingStepViewId.SelfReflection,
  MeetingStepViewId.Evaluation,
  MeetingStepViewId.PastTargets,
  MeetingStepViewId.ContractChanges,
  MeetingStepViewId.MeetingNotes,
  MeetingStepViewId.NewTargets,
]
export const updateAppraisalSteps = [
  MeetingStepViewId.SelfReflection,
  MeetingStepViewId.Evaluation,
  MeetingStepViewId.ContractChanges,
  MeetingStepViewId.NewTargets,
  MeetingStepViewId.MeetingNotes,
  MeetingStepViewId.Payments,
]
